import React, { useState, useEffect, useRef } from "react";
import Footer from "../pages/main/footer";
import Navbar from "../components/Navbar";
import "./OnlineExam.css";
import logobg from "../pages/utils/companylogo.jpeg";
import Constant from "../constant/constant";
import axios from "axios";

const warningBoxStyle = {
  backgroundColor: '#fff3cd',
  border: '1px solid #ffc107',
  borderRadius: '5px',
  padding: '10px 14px',
  margin: '10px 0',
  color: '#856404',
  textAlign: 'left'
};

export default function OnlineExam() {
  const [studentInfo, setStudentInfo] = useState({
    name: "",
    email: "",
    registrationNumber: "",
    department: "",
    year: "",
    batch: "",
  });

  const [started, setStarted] = useState(false);
  const [testBegun, setTestBegun] = useState(false); 
  const [submitted, setSubmitted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(1200); 
  const [startDisabled, setStartDisabled] = useState(false);
  const [questionsData, setQuestionsData] = useState([]);
  const [warningCount, setWarningCount] = useState(0);
  
  // Use refs to track the latest state
  const answersRef = useRef(answers);
  const submittedRef = useRef(false);
  const timerRef = useRef(null);
  const warningCountRef = useRef(0);
  // Violations are ignored until this time (used while the submit confirm dialog is open)
  // Always points at the latest handleAutoSubmit, so effects don't capture a stale copy
  const autoSubmitRef = useRef(() => {});
  const ignoreViolationsUntilRef = useRef(0);

  // Update the ref whenever answers change
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // Update warning count ref
  useEffect(() => {
    warningCountRef.current = warningCount;
  }, [warningCount]);

  // Anti-malpractice: Detect tab switching and window minimization
  useEffect(() => {
    if (!testBegun || submittedRef.current) return;

    const MAX_WARNINGS = 2; // 2 warnings, then the next violation auto-submits
    let alertOpen = false;
    let lastViolationAt = 0;

    const registerViolation = (reason) => {
      if (submittedRef.current || alertOpen) return;
      if (Date.now() < ignoreViolationsUntilRef.current) return;
      // One tab switch fires both "visibilitychange" and "blur"; count it once
      const now = Date.now();
      if (now - lastViolationAt < 1500) return;
      lastViolationAt = now;

      const newWarningCount = warningCountRef.current + 1;
      warningCountRef.current = newWarningCount;
      setWarningCount(newWarningCount);

      // alert() itself blurs the window, so ignore events while it is open
      alertOpen = true;
      if (newWarningCount > MAX_WARNINGS) {
        alert(`You have ${reason} more than ${MAX_WARNINGS} times. Your exam will be submitted automatically.`);
        alertOpen = false;
        lastViolationAt = Date.now();
        autoSubmitRef.current();
      } else {
        alert(`Warning ${newWarningCount}/${MAX_WARNINGS}: Do not switch tabs or leave the exam window! Your exam will be auto-submitted if you do this again after ${MAX_WARNINGS} warnings.`);
        alertOpen = false;
        lastViolationAt = Date.now();
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) registerViolation("switched tabs or minimized the window");
    };

    const handleBlur = () => {
      registerViolation("left the exam window");
    };

    // Prevent right-click
    const preventContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Prevent common keyboard shortcuts
    const preventShortcuts = (e) => {
      // Prevent Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A, Ctrl+P, Ctrl+S, F12, Ctrl+Shift+I
      if (
        (e.ctrlKey && (e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 'a' || e.key === 'p' || e.key === 's')) ||
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') ||
        (e.ctrlKey && e.shiftKey && e.key === 'J') ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    document.addEventListener("contextmenu", preventContextMenu);
    document.addEventListener("keydown", preventShortcuts);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("contextmenu", preventContextMenu);
      document.removeEventListener("keydown", preventShortcuts);
    };
  }, [testBegun]);

  // Timer effect
  useEffect(() => {
    if (!testBegun || submittedRef.current) return;

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          if (!submittedRef.current) {
            autoSubmitRef.current();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [testBegun]);

  // Handle form input change
  const handleChange = (e) => {
    setStudentInfo({ ...studentInfo, [e.target.name]: e.target.value });
  };

  const fetchQuestions = async (batch) => {
    try {
      const res = await axios.get(
        `${Constant.API_BASE_URL}sentinix/api/exam/questions/batch/${batch}`
      );
      if (res.data) {
        setQuestionsData(res.data);
      } else {
        alert("Failed to fetch questions!");
      }
    } catch (err) {
      console.error("Error fetching questions:", err);
      alert("Server error while fetching questions!");
    }
  };
 
  const handleStart = async () => {
    const { name, email, registrationNumber, department, year, batch } = studentInfo;
    if (!name || !email || !registrationNumber || !department || !year || !batch) {
      alert("Please fill all fields!");
      return;
    }
    setStartDisabled(true);
    try {
      const payload = {
        regNo: registrationNumber,
      };

      const response = await axios.get(`${Constant.API_BASE_URL}sentinix/api/exam/online-exams/regNo/${payload.regNo}`);

      if (response.data.success) {
        await fetchQuestions(batch);
        setTimeout(() => setStarted(true), 800);
      } else {
        // alert("This Reg NO is already attended the exam.");
        // setStartDisabled(false);
        await fetchQuestions(batch);
        setTimeout(() => setStarted(true), 800);
      }
    } catch (error) {
      console.error("Error starting exam:", error);
      alert("Server error while starting exam!");
      setStartDisabled(false);
    }
  };

  // Handle answer selection
  const handleAnswer = (qid, index) => {
    setAnswers(prev => {
      const newAnswers = { ...prev, [qid]: index };
      return newAnswers;
    });
  };

  // Separate function for auto-submission (timer expiry)
  const handleAutoSubmit = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitted(true);
    console.log("Auto-submitting with answers:", answersRef.current);
    
    await submitExamData(answersRef.current);
  };

  autoSubmitRef.current = handleAutoSubmit;

  // Separate function for manual submission
  const handleManualSubmit = async () => {
    if (submittedRef.current) return;

    const unanswered = questionsData.length - Object.keys(answers).length;
    const message =
      unanswered > 0
        ? `You have ${unanswered} unanswered question(s).\n\nAre you sure you want to submit the exam? You cannot change your answers after submitting.`
        : "Are you sure you want to submit the exam? You cannot change your answers after submitting.";

    // The confirm dialog blurs the window; don't count that as leaving the exam
    ignoreViolationsUntilRef.current = Infinity;
    const confirmed = window.confirm(message);
    ignoreViolationsUntilRef.current = Date.now() + 1500;
    if (!confirmed) return;

    submittedRef.current = true;
    setSubmitted(true);
    clearInterval(timerRef.current);
    console.log("Manual submit with answers:", answers);
    
    await submitExamData(answers);
  };

  // Common function to submit exam data
  const submitExamData = async (answersToSubmit) => {
    const optionLetters = ["A", "B", "C", "D"];
    const payload = {
      name: studentInfo.name,
      email: studentInfo.email,
      regNo: studentInfo.registrationNumber,
      department: studentInfo.department,
      year: studentInfo.year,
      batch: studentInfo.batch,
    };

    console.log("Submitting answers:", answersToSubmit);
    console.log("Questions data:", questionsData);

    questionsData.forEach((q, index) => {
      const questionNumber = index + 1;
      const answerIndex = answersToSubmit[q.id];
      payload[`qns${questionNumber}`] =
        answerIndex !== undefined ? optionLetters[answerIndex] : "";
      
      console.log(`Q${questionNumber}:`, {
        questionId: q.id,
        answerIndex: answerIndex,
        submittedValue: payload[`qns${questionNumber}`]
      });
    });

    try {
      const response = await axios.post(
        `${Constant.API_BASE_URL}sentinix/api/exam/online-exams/`,
        payload
      );

      if (response.data.success) {
        alert("Exam submitted successfully!");
        window.location.href = "/";
      } else {
        alert("Failed to submit exam. Please try again.");
        // Reset submission state on failure
        submittedRef.current = false;
        setSubmitted(false);
      }
    } catch (error) {
      console.error("Error submitting exam:", error.response?.data || error.message);
      alert("Server error while submitting exam!");
      // Reset submission state on failure
      submittedRef.current = false;
      setSubmitted(false);
    }
  };

  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  if (submitted) {
    return (
      <>
        <Navbar />
        <div className="page-container" style={{ backgroundImage: `url(${logobg})` }}>
          <div className="exam-container centered">
            <h2>✅ Exam Submitted</h2>
            <p>
              Thank you, <b>{studentInfo.name}</b>!
            </p>
            <p style={{ color: "green" }}>
              Your exam has been submitted successfully.
            </p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="page-container" style={{ backgroundImage: `url(${logobg})` }}>
        <div className="exam-container">

          {/* Step 1: Student Form */}
          {!started && (
            <div className="form-box">
              <h2>Online Exam</h2>
              <div className="form-grid">
                {[
                  { label: "Name", name: "name", type: "text" },
                  { label: "Email", name: "email", type: "email" },
                  { label: "Reg No", name: "registrationNumber", type: "text" },
                  { label: "Department", name: "department", type: "text" },
                  { label: "Year", name: "year", type: "text" },
                ].map((field) => (
                  <div className="form-item" key={field.name}>
                    <label>{field.label}:</label>
                    <input
                      type={field.type}
                      name={field.name}
                      value={studentInfo[field.name]}
                      onChange={handleChange}
                    />
                  </div>
                ))}
                <div className="form-item">
                  <label>Batch:</label>
                  <select
                    name="batch"
                    value={studentInfo.batch || ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Batch</option>
                    {["1", "2", "3", "4", "5"].map((b) => (
                      <option key={b} value={b}>Batch {b}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="button-row">
                <button onClick={handleStart} disabled={startDisabled}>
                  {startDisabled ? "Starting..." : "Start Exam"}
                </button>
              </div>
            </div>
          )}

          {/* Student Details + Begin Test button */}
          {started && !testBegun && (
            <div className="student-details-view">
              <div className="student-details-row">
                <p><strong>Name:</strong> {studentInfo.name}</p>
                <p><strong>Email:</strong> {studentInfo.email}</p>
              </div>
              <div className="student-details-row">
                <p><strong>Reg No:</strong> {studentInfo.registrationNumber}</p>
                <p><strong>Department:</strong> {studentInfo.department}</p>
              </div>
              <div className="student-details-row">
                <p><strong>Year:</strong> {studentInfo.year}</p>
                <p><strong>Batch:</strong> {studentInfo.batch}</p>
              </div>
              <div style={warningBoxStyle}>
                <strong>⚠️ Important Instructions</strong>
                <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                  <li>Do not switch tabs, minimize the window or leave the exam page.</li>
                  <li>You will get <strong>2 warnings</strong>. On the next violation your exam will be <strong>submitted automatically</strong>.</li>
                  <li>Right-click, copy/paste and developer shortcuts are disabled.</li>
                  <li>The exam is also submitted automatically when the timer ends.</li>
                </ul>
              </div>
              <div className="begin-test-section">
                <button className="begin-btn" onClick={() => setTestBegun(true)}>
                  Begin Test
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Questions + Timer (after Begin Test) */}
          {started && testBegun && (
            <>
              {/* Student details still visible at top */}
              <div className="student-details-view">
                <div className="student-details-row">
                  <p><strong>Name:</strong> {studentInfo.name}</p>
                  <p><strong>Email:</strong> {studentInfo.email}</p>
                </div>
                <div className="student-details-row">
                  <p><strong>Reg No:</strong> {studentInfo.registrationNumber}</p>
                  <p><strong>Department:</strong> {studentInfo.department}</p>
                </div>
                <div className="student-details-row">
                  <p><strong>Year:</strong> {studentInfo.year}</p>
                  <p><strong>Batch:</strong> {studentInfo.batch}</p>
                </div>
              </div>

              {/* Timer and Warning Counter */}
              <div className="exam-header">
                <div className="timer">⏰ Time Left: {formatTime(timeLeft)}</div>
                <div className="warning-counter" style={{ 
                  color: warningCount >= 2 ? 'red' : warningCount >= 1 ? 'orange' : 'green',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}>
                  ⚠️ Warnings: {Math.min(warningCount, 2)}/2
                </div>
              </div>

              {/* Warning Message */}
              <div style={warningBoxStyle}>
                <strong>⚠️ Do not switch tabs or leave this window.</strong>{' '}
                {warningCount === 0
                  ? "You have 2 warnings; the exam is auto-submitted on the next violation."
                  : warningCount <= 2
                  ? `Warning ${warningCount}/2 used. ${2 - warningCount} warning(s) remaining before auto-submit.`
                  : "Auto-submitting..."}
              </div>

              {/* Questions */}
              <div className="question-list">
                {questionsData.map((q, qIndex) => (
                  <div key={q.id} className="question-box">
                    <h3>
                      Q{qIndex + 1}.
                      <div className="question-text" style={{ whiteSpace: "pre-wrap" }}>
                        {q.question}
                      </div>
                    </h3>

                    <ul>
                      {q.options.map((opt, index) => (
                        <li
                          key={index}
                          className={answers[q.id] === index ? "selected" : ""}
                          onClick={() => handleAnswer(q.id, index)}
                        >
                          {opt}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Submit button */}
              <div className="submit-section">
                <button
                  onClick={handleManualSubmit}
                  className="submit-btn"
                >
                  Submit Exam
                </button>
                <p>Answered: {Object.keys(answers).length} / {questionsData.length}</p>
              </div>
            </>
          )}

        </div>
      </div>
      <Footer />
    </>
  );
} 
